import React, { useContext, useEffect, useState } from 'react'
import validator from 'validator'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'

import HttpClient from '../../services/HttpClient'

import AppContext from '../../contexts/AppContext'

import FormErrors from '../../components/FormErrors/FormErrors'
import Button from '../../components/Button/Button'
import '../../styles/parchment-page.css'
import '../ComingSoon/ComingSoon.css'

/** Formulario con hooks; solo se monta si no hay sesión activa. */
function RegisterForm() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [publicName, setPublicName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [passwordAgain, setPasswordAgain] = useState('')
  const [inviteCode, setInviteCode] = useState(searchParams.get('invite') || '')
  const [inviteStatus, setInviteStatus] = useState(null)
  const [errors, setErrors] = useState([])
  const [comingSoonEnabled, setComingSoonEnabled] = useState(false)

  useEffect(() => {
    const code = searchParams.get('invite')
    if (code) {
      setInviteCode(code)
    }
  }, [searchParams])

  useEffect(() => {
    let cancelled = false

    const loadSettings = async () => {
      try {
        const res = await HttpClient().get('/api/v1/public/settings')
        if (!cancelled) {
          setComingSoonEnabled(!!res.data?.coming_soon_enabled)
        }
      } catch (_error) {
        if (!cancelled) {
          setComingSoonEnabled(false)
        }
      }
    }

    loadSettings()
    return () => {
      cancelled = true
    }
  }, [])

  const validateInvite = async () => {
    if (!inviteCode.trim()) {
      setInviteStatus({ valid: false, message: 'Introduce un código de invitación.' })
      return false
    }

    try {
      const res = await HttpClient().post('/api/v1/public/invites/validate', { inviteCode })
      const valid = !!res.data?.valid
      setInviteStatus({
        valid,
        message: valid ? 'Invitación válida.' : 'Invitación inválida o expirada.',
      })
      return valid
    } catch (_error) {
      setInviteStatus({ valid: false, message: 'No se pudo validar la invitación.' })
      return false
    }
  }

  const onSubmit = async (event) => {
    event.preventDefault()

    setErrors([])
    const nextErrors = []

    if (!publicName) nextErrors.push('Name is required')
    if (!validator.isEmail(email)) nextErrors.push('Email is required.')
    if (!password) nextErrors.push('Password is required.')
    if (!passwordAgain) nextErrors.push('Password does not match.')
    if (password !== passwordAgain) nextErrors.push('Passwords must match.')
    if (comingSoonEnabled && !inviteCode.trim()) nextErrors.push('Invitation code is required.')

    if (nextErrors.length) return setErrors(nextErrors)

    const data = {
      publicName,
      email,
      password,
    }

    if (comingSoonEnabled) {
      data.inviteCode = inviteCode
    }

    try {
      await HttpClient().post('/api/v1/user/register', data)
      navigate('/entrar')
    } catch (e) {
      setErrors([e.response?.data?.message || 'Registration failed'])
    }
  }

  return (
    <div className="page parchment-scope">
      <span className="page__eyebrow">登録 · Registro</span>
      <h1 className="page__title">Crear cuenta</h1>
      <div className="page__panel">
        <form onSubmit={onSubmit} className="form">
          {!!errors.length && <FormErrors errors={errors} />}

          {comingSoonEnabled && (
            <div className="form__group">
              <label className="form__label">Código de invitación</label>
              <input
                type="text"
                className="form__input"
                value={inviteCode}
                onChange={(e) => {
                  setInviteCode(e.target.value)
                  setInviteStatus(null)
                }}
              />
              <button type="button" className="coming-soon__button" onClick={validateInvite}>
                Validar código
              </button>
              {inviteStatus && (
                <p
                  className={`register__invite-status ${
                    inviteStatus.valid ? 'register__invite-status--valid' : 'register__invite-status--invalid'
                  }`}
                >
                  {inviteStatus.message}
                </p>
              )}
            </div>
          )}

          <div className="form__group">
            <label className="form__label">Nombre público</label>
            <input type="text" className="form__input" value={publicName} onChange={(e) => setPublicName(e.target.value)} />
          </div>

          <div className="form__group">
            <label className="form__label">Email</label>
            <input type="text" className="form__input" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>

          <div className="form__group">
            <label className="form__label">Contraseña</label>
            <input type="password" className="form__input" value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>

          <div className="form__group">
            <label className="form__label">Confirmar contraseña</label>
            <input
              type="password"
              className="form__input"
              value={passwordAgain}
              onChange={(e) => setPasswordAgain(e.target.value)}
            />
          </div>

          <Button>Registrarse</Button>
        </form>
      </div>
    </div>
  )
}

export default function Register() {
  const { user } = useContext(AppContext)

  if (user) {
    return <Navigate to="/" />
  }

  return <RegisterForm />
}
